import React from 'react';

const KPI_COLORS: Record<string, { bg: string; shadow: string }> = {
  indigo: { bg: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', shadow: 'rgba(99, 102, 241, 0.3)' },
  green: { bg: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', shadow: 'rgba(16, 185, 129, 0.3)' },
  yellow: { bg: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', shadow: 'rgba(245, 158, 11, 0.3)' },
  red: { bg: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', shadow: 'rgba(239, 68, 68, 0.3)' },
  emerald: { bg: 'linear-gradient(135deg, #059669 0%, #047857 100%)', shadow: 'rgba(5, 150, 105, 0.3)' },
  blue: { bg: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', shadow: 'rgba(59, 130, 246, 0.3)' },
  purple: { bg: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', shadow: 'rgba(139, 92, 246, 0.3)' },
  gray: { bg: 'linear-gradient(135deg, #64748b 0%, #475569 100%)', shadow: 'rgba(100, 116, 139, 0.2)' },
};

const KPI_ICONS: Record<string, string> = {
  kpi_deals_value: '💼',
  kpi_conversations: '💬',
  kpi_pending_tasks: '📋',
  kpi_overdue_tasks: '⚠️',
  kpi_total_sales: '💰',
  kpi_conversion_rate: '📈',
  kpi_leads_today: '🎯',
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);

interface KPIWidgetProps {
  data: any;
  config: Record<string, any>;
  loading: boolean;
  error: string | null;
  widgetType: string;
  width: number;
  height: number;
}

const KPIWidget: React.FC<KPIWidgetProps> = ({ data, config, loading, error, widgetType }) => {
  const colorKey = config.color || 'indigo';
  const colors = KPI_COLORS[colorKey] || KPI_COLORS.indigo;

  // Dynamic color for overdue: red if > 0, gray otherwise
  const effectiveColors = widgetType === 'kpi_overdue_tasks' && data && data.value === 0
    ? KPI_COLORS.gray : colors;

  const formatValue = () => {
    if (!data) return '—';
    if (widgetType === 'kpi_deals_value' || widgetType === 'kpi_total_sales') return formatCurrency(data.value);
    if (widgetType === 'kpi_conversion_rate') return `${(data.value || 0).toFixed(1)}%`;
    return String(data.value ?? 0);
  };

  const getBadge = () => {
    if (!data) return null;
    if (widgetType === 'kpi_deals_value') return `${data.count || 0} abertas`;
    if (widgetType === 'kpi_conversations' && data.unread > 0) return `${data.unread} nao lidas`;
    if (widgetType === 'kpi_pending_tasks') return `${data.completedToday || 0} feitas hoje`;
    if (widgetType === 'kpi_total_sales') return `${data.wonDeals || 0} ganhos`;
    if (widgetType === 'kpi_leads_today') return `${data.thisMonth || 0} este mes`;
    return null;
  };

  if (loading) {
    return (
      <div style={{ ...cardStyle(effectiveColors), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: '24px', height: '24px', border: '3px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ ...cardStyle(KPI_COLORS.gray), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: 'var(--text-sm)', color: 'rgba(255,255,255,0.7)' }}>Erro ao carregar</span>
      </div>
    );
  }

  const badge = getBadge();

  return (
    <div style={cardStyle(effectiveColors)}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-3)' }}>
        <span style={{ fontSize: '28px' }}>{KPI_ICONS[widgetType] || '📊'}</span>
        {badge && (
          <span style={{
            background: 'rgba(255,255,255,0.2)',
            padding: '2px 8px',
            borderRadius: '12px',
            fontSize: '11px',
            color: 'white',
            fontWeight: 600,
          }}>
            {badge}
          </span>
        )}
      </div>
      <div style={{ color: 'white' }}>
        <div style={{ fontSize: 'var(--text-2xl)', fontWeight: 700, marginBottom: '2px' }}>
          {formatValue()}
        </div>
        <div style={{ fontSize: 'var(--text-xs)', opacity: 0.8 }}>
          {data?.label || ''}
        </div>
      </div>
    </div>
  );
};

function cardStyle(colors: { bg: string; shadow: string }): React.CSSProperties {
  return {
    width: '100%',
    height: '100%',
    background: colors.bg,
    borderRadius: 'var(--radius-xl)',
    padding: 'var(--space-5)',
    boxShadow: `0 4px 15px ${colors.shadow}`,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  };
}

export default KPIWidget;
